
import { RouteComponentProps } from "react-router-dom";
import React from 'react';
import AuthenticationService  from '../../_services/AuthenticationService';
import { fetchAppProperties } from '../../_services/AppPropertiesService';
import Config from '../../_config/config';
import { ITeachConfigContext } from '../../_context/PresetContext';
import type { ITeachAppConfig } from '../../_context/PresetContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
// Ported verbatim from ilearn's login so the two apps share one login design
// (.lgn-* component set). The accent/gradient is fixed in that stylesheet — the
// only per-tenant pieces are the hero image and the logo.
import './login-page.css'


interface MyProps extends RouteComponentProps {

   }
   interface Mystate{
       submitted:boolean
       username:string
       password:string
       showPassword:boolean
       debugInfo:any
       debugLoading:boolean
       debugError:string|null
       logoError:boolean
       loading:boolean
       loginError:string|null
       heroUrl:string

   }
 export class Login extends React.Component<MyProps,Mystate> {
    static contextType = ITeachConfigContext;
    returnUrl: string;
    respData:any
    public state: Mystate

    constructor(
        props:MyProps
    ) {
        super(props)
        this.state={
            submitted:false,
            username:'',
            password:'',
            showPassword:false,
            debugInfo:null,
            debugLoading:false,
            debugError:null,
            logoError:false,
            loading:false,
            loginError:null,
            // Same resolution chain ilearn's login uses: a configured hero wins,
            // else the tenant's PWA cover image. If this URL 404s the CSS
            // gradient + solid brand color still render the panel (never blank).
            heroUrl:`https://webfront.edusquares.com/${Config.site}/files/pwa/cover.jpg`
        }
        this.onSubmit = this.onSubmit.bind(this);
        if (AuthenticationService.currentUserValue) {
            this.props.history.push('/')
        }
    }

    onSubmit(e) {
        e.preventDefault()
        if (!this.state.username || !this.state.password) {
            this.setState({ submitted: true });
            return;
        }
        this.setState({submitted:true, loginError:null, loading:true});

        AuthenticationService.login(this.state.username, this.state.password)
        .then((respData:any) => {
            console.log(respData)
            if(respData.data?.status===1) {
                localStorage.setItem('currentUser', JSON.stringify(respData.data?.data));
                AuthenticationService.setCurrentUserValue(respData.data.data);
                const nextParam = new URLSearchParams(window.location.search).get('next');
                this.props.history.push(nextParam && nextParam.startsWith('/') ? nextParam : '/home')
            }
            else{
                this.setState({loading:false, loginError: respData.data?.message || 'Invalid username or password'});
            }
            return respData;
        })
        .catch((e) => {
                    console.log(e.message)
                    this.setState({loading:false, loginError: e.message || 'Unable to connect to server. Please try again.'});
        })
    }

    async componentDidMount() {
        // Pull the tenant's login hero from the same public app-properties.json
        // ilearn reads. iteach checks its own loginPage.iteachHeroImage first
        // (set via Tenant Preferences → Branding), then falls back to the
        // student chain ilearn uses (loginPage.desktopHeroImage[] with
        // BACKGROUND_IMG_URL as the legacy single-URL fallback) so the two
        // apps share one image unless the tenant configures them apart.
        // Silent no-op on failure — the cover-image default from the
        // constructor stays.
        fetchAppProperties().then((props) => {
            const loginPage = props?.loginPage;
            if (!loginPage) return;
            const iteachHero =
                typeof loginPage.iteachHeroImage === "string" ? loginPage.iteachHeroImage.trim() : "";
            const list = loginPage.desktopHeroImage;
            const configured =
                iteachHero ||
                (Array.isArray(list) && list.length > 0 && list[0]) ||
                loginPage.BACKGROUND_IMG_URL ||
                null;
            if (configured) this.setState({ heroUrl: configured });
        }).catch(() => { /* keep the cover-image default */ });

        const urlParams = new URLSearchParams(window.location.search);
        const showDebug = urlParams.has('debug');

        if (showDebug) {
            this.setState({ debugLoading: true, debugError: null });
            try {
                const response = await fetch(`${Config.nodeSiteUrl}v1/debug`);
                const data = await response.json();
                if (data.status === 1) {
                    this.setState({ debugInfo: data.data, debugLoading: false });
                } else {
                    this.setState({ debugError: 'Failed to load debug info', debugLoading: false });
                }
            } catch (error) {
                console.error('Failed to fetch debug info:', error);
                this.setState({
                    debugError: 'NodeFork API not responding. Is it running?',
                    debugLoading: false
                });
            }
        }
    }

    render(){
        const { config, loading: configLoading } = this.context as { config: ITeachAppConfig; loading: boolean };
        const defaultLogoUrl = `https://webfront.edusquares.com/${Config.site}/files/logos/logo.png`;
        const tenantLogoUrl = config.logoUrl || defaultLogoUrl;
        const { showPassword } = this.state;
        const urlParams = new URLSearchParams(window.location.search);
        const showDebug = urlParams.has('debug');

        return(
            <div className="lgn-page">
                {/* ── Brand hero: tenant photo under a fixed brand-gradient overlay
                    (identical treatment to ilearn's .lgn-brand). Solid brand color
                    behind keeps it a clean panel if the image 404s. ── */}
                <div
                    className="lgn-brand"
                    style={{
                        backgroundImage: `linear-gradient(135deg, rgba(16, 84, 158, 0.93) 0%, rgba(14, 140, 184, 0.88) 100%), url(${this.state.heroUrl})`,
                    }}
                >
                    <button
                        type="button"
                        className="lgn-back"
                        aria-label="Go back"
                        onClick={() => this.props.history.push('/')}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <div className="lgn-brand-inner">
                        {!configLoading && !this.state.logoError && (
                            <div className="lgn-logo-chip">
                                <img
                                    src={tenantLogoUrl}
                                    alt="Institute logo"
                                    onError={() => this.setState({ logoError: true })}
                                />
                            </div>
                        )}
                        <h1 className="lgn-brand-title">Welcome back</h1>
                        <p className="lgn-brand-sub">
                            Sign in to manage classes, results and people — all in one place.
                        </p>
                    </div>
                </div>

                {/* ── Form panel ── */}
                <div className="lgn-panel">
                    <div className="lgn-card">
                        <h2 className="lgn-card-title">Sign in</h2>
                        <p className="lgn-card-sub">
                            Enter your username and password to continue.
                        </p>

                        <form onSubmit={this.onSubmit}>
                            <div className={"lgn-field" + (this.state.submitted && !this.state.username ? " lgn-invalid" : "")}>
                                <label htmlFor="login-username">Username</label>
                                <input
                                    id="login-username"
                                    type="text"
                                    placeholder="Enter your username"
                                    name="username"
                                    autoComplete="username"
                                    value={this.state.username}
                                    onChange={(e) => this.setState({ username: e.target.value })}
                                />
                                {this.state.submitted && !this.state.username && (
                                    <span className="lgn-field-hint">Please enter your username</span>
                                )}
                            </div>

                            <div className={"lgn-field" + (this.state.submitted && !this.state.password ? " lgn-invalid" : "")}>
                                <label htmlFor="login-password">Password</label>
                                <div className="lgn-password-wrap">
                                    <input
                                        id="login-password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter your password"
                                        name="password"
                                        autoComplete="current-password"
                                        value={this.state.password}
                                        onChange={(e) => this.setState({ password: e.target.value })}
                                    />
                                    <button
                                        type="button"
                                        className="lgn-eye"
                                        onClick={() => this.setState({ showPassword: !this.state.showPassword })}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        title={showPassword ? "Hide password" : "Show password"}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                                {this.state.submitted && !this.state.password && (
                                    <span className="lgn-field-hint">Please enter your password</span>
                                )}
                            </div>

                            <div className="lgn-links" style={{ justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => this.props.history.push('/forgot-password')}>
                                    Forgot password?
                                </button>
                            </div>

                            {this.state.loginError && (
                                <div className="lgn-error" role="alert">
                                    {this.state.loginError}
                                </div>
                            )}

                            <button type="submit" className="lgn-cta" disabled={this.state.loading}>
                                {this.state.loading ? 'Signing in…' : 'Login'}
                            </button>
                        </form>
                    </div>

                    {!config.hidePoweredBy && (
                        <div className="lgn-powered">
                            <span>Powered by</span>
                            <a
                                href="https://conceptwaves.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {config.poweredByName || 'ConceptWaves'}
                            </a>
                        </div>
                    )}
                </div>

                {/* Debug Info Panel */}
                {showDebug && (
                    <div style={{
                        position: 'fixed',
                        bottom: '10px',
                        right: '10px',
                        background: 'rgba(0, 0, 0, 0.9)',
                        color: 'var(--text-on-brand)',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        zIndex: 9999,
                        maxWidth: '400px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#fbbf24', fontSize: '12px' }}>🔧 Debug Info</div>

                        <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginBottom: '2px' }}>Frontend Config</div>
                            <div style={{ marginLeft: '8px' }}>
                                <div><span style={{ color: 'var(--text-faint)' }}>Tenant:</span> <span style={{ color: 'var(--brand-primary)' }}>{Config.site}</span></div>
                                <div><span style={{ color: 'var(--text-faint)' }}>Version:</span> <span style={{ color: 'var(--brand-primary)' }}>{Config.versionName}</span></div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginBottom: '2px' }}>Backend: TuningFork (Java)</div>
                            <div style={{ marginLeft: '8px' }}>
                                <div><span style={{ color: 'var(--text-faint)' }}>URL:</span> <a href={Config.baseUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)', textDecoration: 'none', wordBreak: 'break-all', fontSize: '10px' }}>{Config.baseUrl}</a></div>
                            </div>
                        </div>

                        <div>
                            <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginBottom: '2px' }}>Backend: NodeFork (Node.js)</div>
                            <div style={{ marginLeft: '8px' }}>
                                <div><span style={{ color: 'var(--text-faint)' }}>API:</span> <a href={Config.nodeSiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)', textDecoration: 'none', wordBreak: 'break-all', fontSize: '10px' }}>{Config.nodeSiteUrl}</a></div>
                                {this.state.debugLoading ? (
                                    <div style={{ marginTop: '4px' }}>
                                        <div style={{ background: 'rgba(var(--brand-primary-rgb), 0.2)', height: '12px', marginBottom: '4px', borderRadius: '3px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
                                        <div style={{ background: 'rgba(var(--brand-primary-rgb), 0.2)', height: '12px', marginBottom: '4px', borderRadius: '3px', animation: 'pulse 1.5s ease-in-out infinite', animationDelay: '0.1s' }}></div>
                                        <div style={{ background: 'rgba(var(--brand-primary-rgb), 0.2)', height: '12px', borderRadius: '3px', animation: 'pulse 1.5s ease-in-out infinite', animationDelay: '0.2s' }}></div>
                                    </div>
                                ) : this.state.debugError ? (
                                    <div style={{ color: 'var(--status-danger)', fontSize: '10px', marginTop: '4px' }}>⚠️ {this.state.debugError}</div>
                                ) : this.state.debugInfo ? (
                                    <>
                                        <div><span style={{ color: 'var(--text-faint)' }}>DB Host:</span> <span style={{ color: 'var(--brand-primary)' }}>{this.state.debugInfo.database.host}:{this.state.debugInfo.database.port}</span></div>
                                        <div><span style={{ color: 'var(--text-faint)' }}>DB Name:</span> <span style={{ color: 'var(--brand-primary)' }}>{this.state.debugInfo.database.name}</span></div>
                                        <div><span style={{ color: 'var(--text-faint)' }}>DB User:</span> <span style={{ color: 'var(--brand-primary)' }}>{this.state.debugInfo.database.username}</span></div>
                                        {this.state.debugInfo.versionInfo && (
                                            <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                                <div style={{ color: '#fbbf24', fontSize: '10px', marginBottom: '4px', fontWeight: 'bold' }}>📋 Version Info (version_info table)</div>
                                                {Object.entries(this.state.debugInfo.versionInfo).map(([key, value]) => (
                                                    <div key={key} style={{ marginLeft: '4px' }}>
                                                        <span style={{ color: 'var(--text-faint)' }}>{key}:</span>{' '}
                                                        <span style={{ color: 'var(--brand-primary)' }}>{value != null ? String(value) : 'NULL'}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                            <div><span style={{ color: 'var(--text-faint)' }}>Environment:</span> <span style={{ color: 'var(--brand-primary)' }}>{this.state.debugInfo.environment}</span></div>
                                            <div><span style={{ color: 'var(--text-faint)' }}>Node:</span> <span style={{ color: 'var(--brand-primary)' }}>{this.state.debugInfo.nodeVersion}</span></div>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}
